import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Space } from "antd";
import { loginEmailResendOtp, loginEmailVerifyOtp } from "../../api";
import { showMessage } from "../../utils/common";
import styles from "./login.module.scss";

const RESEND_OTP_SECONDS = 90;

interface Props {
  email: string;
  sessionId: number;
  onLoginSuccess: (
    accessToken: string,
    refreshToken: string,
    expires: number,
  ) => void;
  onBack: () => void;
}

const LoginOtpVerification: React.FC<Props> = (props: Props) => {
  const { email, sessionId, onLoginSuccess, onBack } = props;
  const [form] = Form.useForm();
  const otpContainerRef = useRef<HTMLDivElement>(null);
  const [filledDigits, setFilledDigits] = useState<number>(0);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [sessionIdState, setSessionIdState] = useState<number>(sessionId);
  const [resendSecondsLeft, setResendSecondsLeft] =
    useState<number>(RESEND_OTP_SECONDS);

  const handleOtpInput = () => {
    // Deferred: on paste, antd's Input.OTP only mutates the pasted-into box
    // synchronously; the other boxes get their values via a React re-render
    // that hasn't committed yet when this native "input" event fires.
    setTimeout(() => {
      const inputs =
        otpContainerRef.current?.querySelectorAll<HTMLInputElement>(
          ".ant-otp-input",
        );
      const filled = inputs
        ? Array.from(inputs).filter((el) => el.value).length
        : 0;
      setFilledDigits(filled);
    }, 0);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setResendSecondsLeft((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleResend = async () => {
    setIsResending(true);
    try {
      const res = await loginEmailResendOtp({
        email,
        session_id: sessionIdState,
      });
      if (res.status === "success") {
        form.resetFields(["otp"]);
        setSessionIdState(res.data.session_id);
        setFilledDigits(0);
        setResendSecondsLeft(RESEND_OTP_SECONDS);
      } else {
        showMessage("error", res.message);
      }
    } catch (error: any) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Gửi lại mã OTP thất bại, vui lòng thử lại sau.",
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async (values: { otp: string }) => {
    setIsVerifying(true);
    try {
      const res = await loginEmailVerifyOtp({
        email,
        code: values.otp,
        session_id: sessionIdState,
      });
      if (res.status === "success") {
        onLoginSuccess(
          res.data.access_token,
          res.data.refresh_token,
          res.data.expires_in,
        );
      } else {
        showMessage("error", res.message);
      }
    } catch (error: any) {
      showMessage(
        "error",
        error.response?.data?.message ||
          "Xác minh OTP thất bại, vui lòng thử lại sau.",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="login container mx-auto mt-16 md:mt-40 mb-16 md:mb-40 main">
      <div className="bg-white mx-auto p-16 max-w-[472px] rounded-sm overflow-hidden">
        <p className="text-2xl mb-16 text-center font-semibold">
          Xác minh bảo mật
        </p>

        <p className="text-center">
          Tài khoản của bạn cần xách minh do không hoạt động trong thời gian
          dài. Vui lòng nhập mã OTP vừa được gửi tới <strong>{email}</strong> để
          tiếp tục.
        </p>

        <Form form={form} size="large" onFinish={handleVerify}>
          <div
            ref={otpContainerRef}
            onInput={handleOtpInput}
            className={`my-24 ${styles.otpInput}`}
          >
            <Form.Item
              name="otp"
              className="mb-0"
              required
              rules={[
                {
                  validator(_, value) {
                    if (!value || value.length < 6) {
                      return Promise.reject(new Error("Vui lòng nhập đủ 6 số"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.OTP
                size="large"
                length={6}
                formatter={(str) => str.replace(/[^\d]/g, "")}
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            block={true}
            htmlType="submit"
            className={styles.btnConfirm}
            disabled={filledDigits < 6}
            loading={isVerifying}
          >
            Xác nhận
          </Button>
        </Form>

        <Space
          className="mt-16 text-xs flex items-center justify-center"
          split="|"
        >
          <Button type="link" onClick={onBack} className="p-0 font-medium">
            Quay lại đăng nhập
          </Button>
          <Button
            type="link"
            disabled={resendSecondsLeft > 0}
            loading={isResending}
            onClick={handleResend}
            className="text-gray-500 p-0 font-medium"
          >
            {resendSecondsLeft > 0
              ? `Gửi lại mã (${resendSecondsLeft}s)`
              : "Gửi lại mã"}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default LoginOtpVerification;
